import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { financeApi, formatCents, type Receivable } from "@/services/finance";
import { PageShell, LoadingState } from "@/shared/ui/PageHeader";
import { Card } from "@/shared/ui/Card";
import { Can, useCan } from "@/auth/permissions";
import { PlatformSettlementsPanel } from "./PlatformSettlementsPanel";   // SETTLE-W1

type SourceTab = "b2b" | "direct" | "api";
type StatusTab = "all" | "draft" | "sent" | "partial" | "overdue";

const SOURCE_TABS: { key: SourceTab; icon: string; label: string }[] = [
  { key: "b2b", icon: "🤝", label: "B2B 客户" },
  { key: "direct", icon: "👤", label: "直客" },
  { key: "api", icon: "🔌", label: "API 平台分账" },
];
const STATUS_TABS: { key: StatusTab; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "draft", label: "待开票" },
  { key: "sent", label: "已开未收" },
  { key: "partial", label: "部分收款" },
  { key: "overdue", label: "逾期" },
];
const SRC_CHIP: Record<SourceTab, string> = { b2b: "text-violet-600", direct: "text-blue-500", api: "text-emerald-600" };

/** 应收账款状态推导（canvas page-72 · 账期内/到期前X天/逾期X天）。 */
function dueState(r: Receivable): { text: string; color: string } {
  if (r.days_overdue > 0) return { text: `逾期 ${r.days_overdue} 天`, color: "text-red-500" };
  const days = Math.ceil((new Date(r.due_at).getTime() - Date.now()) / 86_400_000);
  if (days <= 7) return { text: `到期前 ${Math.max(0, days)} 天`, color: "text-amber-500" };
  return { text: "账期内", color: "text-emerald-500" };
}

/** 财务 · 应收账款 Receivables（/admin/finance/receivables · canvas page-72）。 */
export function ReceivablesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canInvoice = useCan("finance.invoices.create");
  const [params] = useSearchParams();
  // 资金告警面板「逾期应收」深链 ?overdue=1 → 落地即筛逾期（跨源·切 tab 仍保持逾期筛选）
  const [source, setSource] = useState<SourceTab>("b2b");
  const [status, setStatus] = useState<StatusTab>(params.get("overdue") === "1" ? "overdue" : "all");
  const [search, setSearch] = useState("");

  const allQ = useQuery({ queryKey: ["finance", "receivables", "all"], queryFn: () => financeApi.receivables() });

  // 标已收 · 真实状态变更（markPaid）
  const markPaid = useMutation({
    mutationFn: (r: Receivable) =>
      financeApi.markPaid(r.id, { amount_cents: r.amount_cents - r.paid_cents, paid_at: new Date().toISOString().slice(0, 10) }),
    onSuccess: () => {
      // E2 跨块联动：待收 KPI(overview)+应收明细+总览告警逾期(dashboard) 都跟着变
      qc.invalidateQueries({ queryKey: ["finance", "receivables"] });
      qc.invalidateQueries({ queryKey: ["finance", "overview"] });
      qc.invalidateQueries({ queryKey: ["finance", "dashboard"] });
    },
    onError: (e) => alert((e as Error).message || "标已收失败"),
  });

  // Invoice 按钮 · 联动「记录新 Invoice」(prefill) / 已开则查看
  const onInvoice = (r: Receivable) => {
    if (r.status === "draft") {
      const q = new URLSearchParams({ order_no: r.order_no ?? "", customer: r.customer, customer_kind: r.source, amount_cents: String(r.amount_cents), currency: r.currency });
      navigate(`/admin/finance/invoices/new?${q.toString()}`);
    } else {
      navigate(`/admin/finance/invoices?invoice_status=${r.status}`);
    }
  };

  // E1 口径：作废发票从应收排除（与总览待收一致·作废永久留在发票管理「作废」列表里查）
  const all = (allQ.data ?? []).filter((r) => r.status !== "void");
  const countBy = (s: SourceTab) => all.filter((r) => r.source === s).length;
  // KPI 聚合
  const totalAr = all.reduce((s, r) => s + r.amount_cents, 0);
  const totalPaid = all.reduce((s, r) => s + r.paid_cents, 0);
  const draftCount = all.filter((r) => r.status === "draft").length;
  const overdue = all.filter((r) => r.days_overdue > 0);
  const overdueSum = overdue.reduce((s, r) => s + (r.amount_cents - r.paid_cents), 0);

  // 列表过滤
  const rows = all
    .filter((r) => r.source === source)
    .filter((r) => status === "all" || (status === "overdue" ? r.days_overdue > 0 : r.status === status))
    .filter((r) => !search || r.customer.includes(search) || (r.order_no ?? "").includes(search) || r.invoice_no.includes(search));

  const kpis = [
    { label: "本月应收", value: formatCents(totalAr), sub: `${all.length} 笔待处理`, color: "text-blue-500" },
    { label: "已收款", value: formatCents(totalPaid), sub: `${totalAr ? Math.round((totalPaid / totalAr) * 100) : 0}% 已到账`, color: "text-emerald-500" },
    { label: "待开 Invoice", value: String(draftCount), sub: "🔴 财务处理", color: "text-amber-500" },
    { label: "逾期", value: formatCents(overdueSum), sub: `🔴 ${overdue.length} 客户`, color: "text-red-500" },
  ];

  return (
    <PageShell
      title="💰 应收账款 · Receivables"
      subtitle="3 来源：B2B 客户 / 直客 / API 平台分账 · 开 invoice / 作废只在本页"
      actions={
        <div className="flex gap-2">
          <button className="flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-[12px] font-medium text-slate-700 hover:bg-slate-50">USD ▾</button>
          <Can perm="finance.invoices.create"><button className="flex h-9 items-center rounded-lg bg-blue-600 px-3 text-[12px] font-semibold text-white hover:bg-blue-700">批量开 Invoice</button></Can>
        </div>
      }
    >
      <div className="rounded-lg bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-800">🔒 经理可见：仅 B2B 客户 tab + 直客 tab（与自己负责客户相关）· API 平台分账仅老板/财务</div>

      {/* 4 KPI（API 平台分账 tab 有自己的汇总四格） */}
      {source !== "api" && <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[11px] font-semibold text-slate-500">{k.label}</div>
            <div className={`mt-1.5 text-[22px] font-bold leading-none ${k.color}`}>{k.value}</div>
            <div className="mt-1.5 text-[10px] text-slate-400">{k.sub}</div>
          </div>
        ))}
      </div>}

      {/* 主列表 */}
      <Card>
        {/* 来源 tab */}
        <div className="mb-3 flex flex-wrap items-center gap-4 border-b border-slate-100 pb-3">
          {SOURCE_TABS.map((t) => (
            <button key={t.key} onClick={() => setSource(t.key)} className={`text-[13px] ${source === t.key ? "font-bold text-blue-600" : "font-medium text-slate-500 hover:text-slate-700"}`}>
              {t.icon} {t.label}{t.key === "api" ? "" : ` ${countBy(t.key)}`}
            </button>
          ))}
        </div>
        {source === "api" ? <PlatformSettlementsPanel /> : (<>
        {/* 状态子 tab + 搜索 */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {STATUS_TABS.map((t) => (
            <button key={t.key} onClick={() => setStatus(t.key)} className={`h-8 rounded-lg px-3 text-[12px] ${status === t.key ? "bg-blue-600 font-semibold text-white" : "font-medium text-slate-700 hover:bg-slate-100"}`}>{t.label}</button>
          ))}
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔍 搜索客户/订单号" className="ml-auto h-8 w-48 rounded-lg border border-slate-200 px-3 text-[12px] focus:border-blue-500 focus:outline-none" />
        </div>

        {allQ.isLoading ? <LoadingState /> : allQ.isError ? (
          <div className="rounded-lg border border-red-200 bg-red-50/50 p-6 text-center">
            <p className="text-[13px] font-semibold text-red-700">应收数据加载失败</p>
            <p className="mt-0.5 text-[11px] text-red-500">{(allQ.error as Error)?.message || "网络错误"}</p>
            <button onClick={() => allQ.refetch()} disabled={allQ.isFetching} className="mt-2 rounded-md bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">{allQ.isFetching ? "重试中…" : "点击重试"}</button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-[13px]">
              <tbody>
                {rows.map((r) => {
                  const st = dueState(r);
                  return (
                    <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className={`text-[10px] font-bold ${SRC_CHIP[r.source]}`}>{r.source.toUpperCase()}</div>
                        <div className="text-[11px] font-semibold text-blue-600">{r.order_no ?? "—"}</div>
                        <div className="text-[9px] text-slate-400">订单号</div>
                      </td>
                      <td className="px-4 py-3 text-[12px] font-semibold text-slate-900">{r.customer}{r.settlement_terms ? ` · ${r.settlement_terms}` : ""}</td>
                      <td className="px-4 py-3">
                        <div className="text-[14px] font-bold text-emerald-500">{formatCents(r.amount_cents, r.currency)}</div>
                        <div className="text-[9px] text-slate-400">含税 · 未收 {formatCents(r.amount_cents - r.paid_cents, r.currency)}</div>
                      </td>
                      <td className={`px-4 py-3 text-[10px] font-bold ${st.color}`}>{st.text}</td>
                      <td className="px-4 py-3 text-[10px] font-bold text-slate-600">{r.status === "draft" ? "待开 invoice" : `已开 ${r.invoice_no}`}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1.5">
                          {(r.status !== "draft" || canInvoice) && <button onClick={() => onInvoice(r)} className="rounded-md bg-blue-600 px-2 py-1 text-[12px] font-semibold text-white hover:bg-blue-700">{r.status === "draft" ? "开 Invoice" : "Invoice"}</button>}
                          <Can perm="finance.payments.record"><button onClick={() => markPaid.mutate(r)} disabled={r.status === "paid" || markPaid.isPending} className="rounded-md border border-slate-300 px-2 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40">标已收</button></Can>
                          <button type="button" disabled title="规划中" className="cursor-not-allowed rounded-md border border-slate-200 px-2 py-1 text-[12px] font-medium text-slate-400 opacity-60">催款（规划中）</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && <tr><td className="px-4 py-6 text-center text-slate-400">该来源/状态下暂无应收</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        </>)}
      </Card>
    </PageShell>
  );
}
